/**
 * Headless after-call workflow build. Runs the same builder brain the workflow
 * builder page uses (`/api/ai/workflow-chat`) without navigating there: create
 * a draft, stream one build turn, publish, subscribe the Gail webhook. The
 * chat card that triggered the handoff reports progress and the operator stays
 * in the conversation. See docs/workflow-ai-chat/README.md.
 */

import {
  checkWorkflow,
  createDraftWorkflow,
  getWorkflow,
  publishWorkflow,
  saveDraftWorkflow,
} from "@/actions/workflows/actions";
import {
  fetchGailWebhooksForWorkflow,
  syncGailWebhooksForWorkflow,
} from "@/actions/webhooks/actions";
import { buildAutomatedChatPrompt } from "@/components/workflows/ai-chat/workflowBuildSeed";
import {
  normalizeStartPayloadKeys,
  START_NODE_ID,
} from "@/components/workflows/utils/helpers/workflow-graph-operations";
import type {
  OmegaGraph,
  StreamEvent,
} from "@/components/workflows/utils/helpers/workflow-chat-types";
import { WebhookEventType } from "@/enums/settings";
import { tryRefreshToken } from "@/lib/api";
import type { OmegaNodeStart, Workflow, WorkflowNode } from "@/types/workflows";

export type AfterCallBuildStage = "creating" | "building" | "publishing";

/**
 * Where the build stopped. Each maps to its own translated card message.
 * `streamError` means the builder stream reported an error or ended without
 * its terminal `done` event, so whatever graph it emitted is not trusted.
 * `webhooks` means the workflow is published but the Gail subscription failed
 * or could not be confirmed, so the card still offers to open it.
 */
export type AfterCallBuildFailure =
  | "draft"
  | "builder"
  | "streamError"
  | "noGraph"
  | "check"
  | "publish"
  | "webhooks";

export type AfterCallBuildResult =
  | { ok: true; workflow: { id: string; name: string } }
  | {
      ok: false;
      reason: AfterCallBuildFailure;
      /** Draft (or published workflow, for `webhooks`) the operator can open. */
      workflowId?: string;
      detail?: string;
    };

export interface AfterCallBuildParams {
  /** Card title; becomes the draft name until `set_workflow_details` renames it. */
  name: string;
  /** Natural-language description of the automation, from the `build_workflow` tool. */
  prompt: string;
  /** Draft left by an earlier failed attempt; reused instead of creating a second one. */
  existingWorkflowId?: string;
  onStage?: (stage: AfterCallBuildStage) => void;
  /**
   * Fires as soon as the draft exists (created or reused), before the long
   * build stream. Callers persist the link here so a reload or a thrown fetch
   * mid-build still finds the draft instead of creating a duplicate.
   */
  onDraftCreated?: (workflowId: string) => void;
  fetchImpl?: typeof fetch;
}

export interface BuildTurn {
  graph: OmegaGraph | null;
  name?: string;
  description?: string;
  error?: string;
  /** The route's terminal `done` event arrived; without it the stream was cut short. */
  done: boolean;
}

/**
 * The only trigger an after-call automation may publish with. The model is
 * told to set it but sometimes picks another call event or none at all.
 */
export const AFTER_CALL_GAIL_EVENTS: readonly WebhookEventType[] = [
  WebhookEventType.CallCompleted,
];

/** Mirrors the builder's empty canvas: a lone Start node the AI chains from. */
export function emptyStartGraph(): OmegaGraph {
  const start: OmegaNodeStart = {
    id: START_NODE_ID,
    type: "start",
    name: "Start",
    displayInfo: { position: { x: 50, y: 50 } },
  };
  return { nodes: [start], edges: [] };
}

/**
 * Makes the Start node listen for processed calls regardless of what the model
 * set. Both `webhook.isEnabled` and `metadata.gailWebhookEvents` must be set
 * for Omega to subscribe, and the event list is replaced with exactly
 * `call_processed`: a model-chosen `call_failed` or `conversation_started`
 * would otherwise publish an after-call automation that never fires after a
 * call. Other Start config (secret, toolId, ...) is left alone.
 */
export function ensureAfterCallStartTrigger(graph: OmegaGraph): OmegaGraph {
  return {
    ...graph,
    nodes: graph.nodes.map((node): WorkflowNode => {
      if (node.type !== "start") return node;
      return {
        ...node,
        webhook: { ...node.webhook, isEnabled: true },
        metadata: {
          ...node.metadata,
          gailWebhookEvents: [...AFTER_CALL_GAIL_EVENTS],
        },
      };
    }),
  };
}

/** Events the published Start node subscribes to; what `syncGailWebhooksForWorkflow` needs. */
export function gailWebhookEventsOf(graph: OmegaGraph): WebhookEventType[] {
  const start = graph.nodes.find((node): node is OmegaNodeStart => node.type === "start");
  return start?.metadata?.gailWebhookEvents ?? [];
}

function parseStreamEvent(line: string): StreamEvent | null {
  const trimmed = line.trim();
  if (!trimmed) return null;
  try {
    return JSON.parse(trimmed) as StreamEvent;
  } catch {
    return null;
  }
}

function applyStreamEvent(turn: BuildTurn, event: StreamEvent): void {
  switch (event.type) {
    case "graph":
      turn.graph = event.graph;
      break;
    case "workflow-details":
      if (event.name?.trim()) turn.name = event.name.trim();
      if (event.description?.trim()) turn.description = event.description.trim();
      break;
    case "error":
      turn.error = event.message;
      break;
    case "done":
      turn.done = true;
      break;
    default:
      break;
  }
}

/**
 * Reads the NDJSON build stream, keeping the last graph and details it
 * emitted plus whether it ended with `done` or `error`. The route sends
 * exactly one of those two as its final event before closing.
 */
export async function readBuildStream(body: ReadableStream<Uint8Array>): Promise<BuildTurn> {
  const turn: BuildTurn = { graph: null, done: false };
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let done = false;
  while (!done) {
    const { value, done: readerDone } = await reader.read();
    done = readerDone;
    if (!value) continue;
    buffer += decoder.decode(value, { stream: !done });
    const lines = buffer.split("\n");
    buffer = lines.pop() ?? "";
    for (const line of lines) {
      const event = parseStreamEvent(line);
      if (event) applyStreamEvent(turn, event);
    }
  }
  const last = parseStreamEvent(buffer);
  if (last) applyStreamEvent(turn, last);
  return turn;
}

async function resolveDraft(
  params: AfterCallBuildParams
): Promise<{ draft: Workflow; reused: boolean } | null> {
  if (params.existingWorkflowId) {
    const existing = await getWorkflow(params.existingWorkflowId);
    if (existing) return { draft: existing, reused: true };
  }
  const created = await createDraftWorkflow(params.name, params.prompt);
  return created.data?.id ? { draft: created.data, reused: false } : null;
}

async function runBuildTurn(
  params: AfterCallBuildParams,
  draft: Workflow,
  currentGraph: OmegaGraph
): Promise<BuildTurn | { failed: "builder"; detail?: string }> {
  const fetchImpl = params.fetchImpl ?? fetch;
  // Same as the builder panel: top up the session cookie before opening a
  // long stream, since a token that expires mid-stream cannot be recovered.
  await tryRefreshToken();
  const response = await fetchImpl("/api/ai/workflow-chat", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      messages: [
        {
          role: "user",
          content: buildAutomatedChatPrompt({
            prompt: params.prompt,
            autoPublish: true,
            purpose: "after_call",
          }),
        },
      ],
      currentGraph,
      hasPublishedVersion: false,
      workflowId: draft.id,
    }),
  });
  if (!response.ok || !response.body) {
    const detail = await response.text().catch(() => undefined);
    return { failed: "builder", detail };
  }
  return readBuildStream(response.body);
}

/**
 * A stream that reported `error` (even after emitting a graph) or that ended
 * without the route's terminal `done` event did not finish the build turn;
 * publishing its partial graph would ship a half-built automation.
 */
function streamFailure(turn: BuildTurn): string | null {
  if (turn.error) return turn.error;
  if (!turn.done) return "The workflow builder stream ended before it signalled completion.";
  return null;
}

/**
 * `syncGailWebhooksForWorkflow` goes through `apiFetch`, which resolves `null`
 * on HTTP and network failures instead of throwing, so a failed create would
 * otherwise look like success. Re-read the subscriptions and require every
 * event the Start node listens for to be present on the workflow's hook URL.
 */
async function verifyGailWebhooks(
  workflowId: string,
  events: WebhookEventType[]
): Promise<string | null> {
  const subscribed = new Set((await fetchGailWebhooksForWorkflow(workflowId)).map((wh) => wh.event));
  const missing = events.filter((event) => !subscribed.has(event));
  return missing.length > 0
    ? `Gail webhook subscription missing for: ${missing.join(", ")}`
    : null;
}

/**
 * Drives one after-call build end to end and reports where it stopped. Never
 * throws for expected failures; the card turns each `reason` into a message.
 */
export async function runAfterCallWorkflowBuild(
  params: AfterCallBuildParams
): Promise<AfterCallBuildResult> {
  params.onStage?.("creating");
  const resolved = await resolveDraft(params);
  if (!resolved) return { ok: false, reason: "draft" };
  const { draft } = resolved;
  params.onDraftCreated?.(draft.id);

  params.onStage?.("building");
  const startingGraph = draft.graph?.nodes?.length ? draft.graph : emptyStartGraph();
  const turn = await runBuildTurn(params, draft, startingGraph);
  if ("failed" in turn) {
    return { ok: false, reason: turn.failed, workflowId: draft.id, detail: turn.detail };
  }
  const streamError = streamFailure(turn);
  if (streamError) {
    return { ok: false, reason: "streamError", workflowId: draft.id, detail: streamError };
  }
  if (!turn.graph) {
    // The model only asked a question and never mutated the graph.
    return { ok: false, reason: "noGraph", workflowId: draft.id };
  }

  params.onStage?.("publishing");
  const graph = ensureAfterCallStartTrigger(normalizeStartPayloadKeys(turn.graph));
  const toSave: Workflow = {
    ...draft,
    name: turn.name ?? draft.name,
    description: turn.description ?? draft.description,
    graph,
    revision: draft.revision || undefined,
  };
  // Save first so a failed check or publish still leaves an openable draft.
  const saved = await saveDraftWorkflow(toSave);
  if (!saved.data) {
    return { ok: false, reason: "publish", workflowId: draft.id, detail: saved.error };
  }
  const revision = saved.data.revision;
  const toPublish: Workflow = {
    ...toSave,
    ...(revision && revision > 1 ? { revision } : { revision: undefined }),
  };

  const checked = await checkWorkflow(toPublish);
  if (!checked.data) {
    return { ok: false, reason: "check", workflowId: draft.id, detail: checked.error };
  }
  const published = await publishWorkflow(toPublish);
  if (!published.data) {
    return { ok: false, reason: "publish", workflowId: draft.id, detail: published.error };
  }

  const events = gailWebhookEventsOf(graph);
  try {
    await syncGailWebhooksForWorkflow(published.data.id, events);
    const unverified = await verifyGailWebhooks(published.data.id, events);
    if (unverified) {
      return { ok: false, reason: "webhooks", workflowId: published.data.id, detail: unverified };
    }
  } catch (error) {
    return {
      ok: false,
      reason: "webhooks",
      workflowId: published.data.id,
      detail: error instanceof Error ? error.message : undefined,
    };
  }

  return { ok: true, workflow: { id: published.data.id, name: published.data.name } };
}
